"use client";
import { motion } from "framer-motion";
import { productCardVariants } from "@/lib/animations";

interface YachtCardSkeletonProps {
    index?: number;
    viewType?: 'box' | 'horizontal';
}

export default function YachtCardSkeleton({ index = 0, viewType = 'box' }: YachtCardSkeletonProps) {
    return (
        <motion.div
            variants={productCardVariants}
            initial="hidden"
            animate="visible"
            transition={{
                delay: index * 0.1, // Stagger efekti için
            }}
            className={`relative font-sans flex ${viewType === 'box' ? 'flex-col rounded-tr-lg' : 'flex-row bg-white rounded-lg p-4 shadow-tour-card'}`}
        >
            {/* Image skeleton */}
            <div className={`relative overflow-hidden h-[250px] ${viewType === 'box' ? 'w-full rounded-tl-lg rounded-tr-lg' : 'w-4/12 shrink-0 rounded-lg'}`}>
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
                {/* Image count badges skeleton (for box view) */}
                {viewType === 'box' && (
                    <div className="absolute top-4 right-4 flex gap-2">
                        <div className="w-12 h-8 bg-gray-400 rounded-lg animate-pulse" />
                        <div className="w-12 h-8 bg-gray-400 rounded-lg animate-pulse" />
                    </div>
                )}
            </div>
            
            {/* Content skeleton */}
            <div className={`relative flex flex-col justify-between ${viewType === 'box' ? 'px-5 pt-10 pb-5 bg-white shadow-tour-card -mt-2.5 rounded-lg' : 'w-full h-full py-2.5 px-6'}`}>
                {/* Tag skeleton */}
                {viewType === 'box' && (
                    <div className="absolute right-4 -top-[15px] w-28 h-10 bg-gray-400 rounded-lg animate-pulse" />
                )}
                
                {/* Title skeleton */}
                <div className="space-y-3 flex flex-col">
                    <div className="h-7 bg-gray-300 rounded mb-2 animate-pulse" style={{ width: '85%' }} />
                    <div className="h-7 bg-gray-300 rounded mb-2 animate-pulse" style={{ width: '70%' }} />
                    
                    {/* Location info skeleton */}
                    <div className="flex w-full gap-3 items-center mt-2">
                        <div className="w-9 h-8 bg-gray-200 rounded animate-pulse shrink-0" />
                        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '65%' }} />
                    </div>
                    
                    {/* Details skeleton */}
                    <div className={`text-sm mt-3 ${viewType === 'box' ? 'flex flex-wrap items-center gap-3' : 'grid grid-cols-2 gap-3'}`}>
                        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '85px' }} />
                        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '105px' }} />
                        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '95px' }} />
                        {/* Minimum rental duration skeleton */}
                        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '110px' }} />
                    </div>
                </div>
                
                {/* Price skeleton */}
                <div className={`${viewType === 'box' ? 'mt-4' : 'justify-end'} flex flex-col pt-3 border-t border-alsero-light-gray/20 gap-2`}>
                    <div className="flex gap-2 items-end">
                        <div className="h-4 w-14 bg-gray-200 rounded animate-pulse" />
                        <div className="h-7 w-28 bg-gray-300 rounded animate-pulse" />
                    </div>
                    {/* Minimum rental info skeleton */}
                    <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                </div>
            </div>
        </motion.div>
    );
}

